import { useState, useCallback, useRef, useEffect } from 'react';
import { validateField } from '../../../lib/utils/validationUtils';

interface QuickFillItem {
  Name: string;
  [key: string]: string | undefined;
}

interface QuickFillProperty {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  checked: boolean;
  isReadOnly?: boolean;
  dataType?: string;
}

interface HistorySnapshot {
  data: QuickFillItem[];
  editedCells: Record<string, boolean>;
}

interface ChangeSummaryEntry {
  oldValue: string;
  newValue: string;
}

// Bounds memory: rows are only ever updated immutably, so snapshots can share
// unmodified row object references instead of deep-cloning the whole dataset.
const MAX_HISTORY_ENTRIES = 200;

const truncateHistory = (snapshots: HistorySnapshot[]): HistorySnapshot[] =>
  snapshots.length > MAX_HISTORY_ENTRIES
    ? snapshots.slice(snapshots.length - MAX_HISTORY_ENTRIES)
    : snapshots;

const valuesEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true;
  if (
    left !== null &&
    right !== null &&
    typeof left === 'object' &&
    typeof right === 'object'
  ) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }
  return false;
};

const areRowsEqual = (left: QuickFillItem | undefined, right: QuickFillItem | undefined): boolean => {
  if (!left || !right) return left === right;

  const keys = new Set<string>([
    ...Object.keys(left as Record<string, unknown>),
    ...Object.keys(right as Record<string, unknown>)
  ]);

  for (const key of keys) {
    const leftValue = (left as Record<string, unknown>)[key];
    const rightValue = (right as Record<string, unknown>)[key];
    if (!valuesEqual(leftValue, rightValue)) {
      return false;
    }
  }

  return true;
};

const areSnapshotsEqual = (left: QuickFillItem[], right: QuickFillItem[]): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index++) {
    if (!areRowsEqual(left[index], right[index])) {
      return false;
    }
  }
  return true;
};

const hasSameRowStructure = (left: QuickFillItem[], right: QuickFillItem[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index++) {
    if ((left[index]?.Name ?? '') !== (right[index]?.Name ?? '')) {
      return false;
    }
  }

  return true;
};

const buildEditedCellsFromBaseline = (
  baseline: QuickFillItem[],
  current: QuickFillItem[]
): Record<string, boolean> => {
  const edited: Record<string, boolean> = {};

  for (let rowIndex = 0; rowIndex < current.length; rowIndex++) {
    const baselineRow = baseline[rowIndex];
    const currentRow = current[rowIndex];
    if (!baselineRow || !currentRow) {
      continue;
    }

    const keys = new Set<string>([
      ...Object.keys(baselineRow as Record<string, unknown>),
      ...Object.keys(currentRow as Record<string, unknown>),
    ]);

    keys.forEach((key) => {
      if (key === 'Name' || key.startsWith('__')) {
        return;
      }

      const baselineValue = (baselineRow as Record<string, unknown>)[key];
      const currentValue = (currentRow as Record<string, unknown>)[key];
      if (!valuesEqual(baselineValue, currentValue)) {
        edited[`${rowIndex}-${key}`] = true;
      }
    });
  }

  return edited;
};

export interface UseTableHistoryReturn {
  editedCells: Record<string, boolean>;
  validationErrors: Record<string, string>;
  historyIndex: number;
  historyLength: number;
  changeSummary: Record<string, ChangeSummaryEntry>;
  handleCellChange: (index: number, field: keyof QuickFillItem, value: string) => void;
  handleUndo: () => void;
  handleRedo: () => void;
}

export function useTableHistory(
  safeData: QuickFillItem[],
  onRowChange: (index: number, field: keyof QuickFillItem, value: string) => void,
  selectedProperties?: QuickFillProperty[]
): UseTableHistoryReturn {
  const [editedCells, setEditedCells] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyInitializedRef = useRef(false);
  const [changeSummary, setChangeSummary] = useState<Record<string, ChangeSummaryEntry>>({});
  const historyIndexRef = useRef(-1);
  const editedCellsRef = useRef<Record<string, boolean>>({});
  const applyingSnapshotRef = useRef<QuickFillItem[] | null>(null);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    editedCellsRef.current = editedCells;
  }, [editedCells]);

  // Keep history synchronized with data snapshots.
  // If data changes outside table editing (auto-fill/import/reload), reset history baseline.
  useEffect(() => {
    // Snapshots share row references (rows are never mutated in place), so no
    // per-change deep clone is needed here.
    const currentData = safeData;

    if (!historyInitializedRef.current) {
      historyInitializedRef.current = true;
      const initialState: HistorySnapshot = { data: currentData, editedCells: {} };
      setHistory([initialState]);
      setHistoryIndex(0);
      historyIndexRef.current = 0;
      return;
    }

    const pendingSnapshot = applyingSnapshotRef.current;
    if (pendingSnapshot) {
      if (areSnapshotsEqual(currentData, pendingSnapshot)) {
        applyingSnapshotRef.current = null;
      }
      return;
    }

    if (historyIndex < 0 || history.length === 0) {
      setHistory([{ data: currentData, editedCells: {} }]);
      setHistoryIndex(0);
      historyIndexRef.current = 0;
      editedCellsRef.current = {};
      setEditedCells({});
      setValidationErrors({});
      setChangeSummary({});
      return;
    }

    const activeSnapshot = history[historyIndex];
    if (!activeSnapshot) return;

    if (!areSnapshotsEqual(currentData, activeSnapshot.data)) {
      const baselineSnapshot = history[0];
      const sameStructure = Boolean(
        baselineSnapshot &&
        hasSameRowStructure(currentData, activeSnapshot.data) &&
        hasSameRowStructure(currentData, baselineSnapshot.data)
      );

      if (sameStructure && baselineSnapshot) {
        const nextEditedCells = buildEditedCellsFromBaseline(baselineSnapshot.data, currentData);
        const nextSnapshot: HistorySnapshot = {
          data: currentData,
          editedCells: nextEditedCells,
        };

        setHistory((prev) => {
          const currentIndex = historyIndexRef.current;
          const truncated = currentIndex < prev.length - 1 ? prev.slice(0, currentIndex + 1) : prev;
          return truncateHistory([...truncated, nextSnapshot]);
        });
        setHistoryIndex((prev) => {
          const appendedLength = prev + 2;
          const dropped = Math.max(0, appendedLength - MAX_HISTORY_ENTRIES);
          const nextIndex = prev + 1 - dropped;
          historyIndexRef.current = nextIndex;
          return nextIndex;
        });
        editedCellsRef.current = nextEditedCells;
        setEditedCells(nextEditedCells);
        setValidationErrors({});
        return;
      }

      setHistory([{ data: currentData, editedCells: {} }]);
      setHistoryIndex(0);
      historyIndexRef.current = 0;
      editedCellsRef.current = {};
      setEditedCells({});
      setValidationErrors({});
      setChangeSummary({});
    }
  }, [safeData, history, historyIndex]);

  const handleCellChange = useCallback((index: number, field: keyof QuickFillItem, value: string) => {
    const row = safeData[index];
    if (!row) return;

    const oldValue = String(row[field] ?? '');
    if (oldValue === value) return;

    // Get dataType from selected properties for validation
    const columnProperty = Array.isArray(selectedProperties)
      ? selectedProperties.find(p => p && p.label === field)
      : undefined;
    const dataType = columnProperty?.dataType;

    // Validate the value
    const validationResult = validateField(field, value, dataType);
    const cellId = `${index}-${field}`;

    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (validationResult.isValid) {
        delete newErrors[cellId];
      } else {
        newErrors[cellId] = validationResult.error!;
      }
      return newErrors;
    });

    const nextEditedCells = { ...editedCellsRef.current, [cellId]: true };
    const nextData = safeData.slice();
    nextData[index] = { ...nextData[index], [field]: value };

    applyingSnapshotRef.current = nextData;
    setHistory((prev) => {
      const currentIndex = historyIndexRef.current;
      const truncated = currentIndex < prev.length - 1 ? prev.slice(0, currentIndex + 1) : prev;
      return truncateHistory([
        ...truncated,
        {
          data: nextData,
          editedCells: nextEditedCells,
        }
      ]);
    });
    setHistoryIndex((prev) => {
      const appendedLength = prev + 2;
      const dropped = Math.max(0, appendedLength - MAX_HISTORY_ENTRIES);
      const nextIndex = prev + 1 - dropped;
      historyIndexRef.current = nextIndex;
      return nextIndex;
    });

    onRowChange(index, field, value);

    editedCellsRef.current = nextEditedCells;
    setEditedCells(nextEditedCells);
    setChangeSummary(prev => ({ ...prev, [cellId]: { oldValue, newValue: value } }));
  }, [onRowChange, safeData, selectedProperties]);

  const applySnapshot = useCallback((targetData: QuickFillItem[]) => {
    if (safeData.length !== targetData.length) {
      applyingSnapshotRef.current = null;
      return;
    }

    applyingSnapshotRef.current = targetData;

    for (let rowIndex = 0; rowIndex < safeData.length; rowIndex++) {
      const currentRow = safeData[rowIndex];
      const targetRow = targetData[rowIndex];
      if (!currentRow || !targetRow) continue;

      const keys = new Set<string>([
        ...Object.keys(currentRow as Record<string, unknown>),
        ...Object.keys(targetRow as Record<string, unknown>)
      ]);

      for (const key of keys) {
        if (key === 'Name' || key.startsWith('__')) continue;

        const currentValue = String((currentRow as Record<string, unknown>)[key] ?? '');
        const targetValue = String((targetRow as Record<string, unknown>)[key] ?? '');

        if (currentValue !== targetValue) {
          onRowChange(rowIndex, key as keyof QuickFillItem, targetValue);
        }
      }
    }
  }, [onRowChange, safeData]);

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;

    const targetIndex = historyIndex - 1;
    const prevState = history[targetIndex];
    if (!prevState) return;

    applySnapshot(prevState.data);
    editedCellsRef.current = { ...prevState.editedCells };
    setEditedCells(prevState.editedCells);
    setChangeSummary({});
    setHistoryIndex(targetIndex);
    historyIndexRef.current = targetIndex;
  }, [history, historyIndex, applySnapshot]);

  const handleRedo = useCallback(() => {
    if (historyIndex < 0 || historyIndex >= history.length - 1) return;

    const targetIndex = historyIndex + 1;
    const nextState = history[targetIndex];
    if (!nextState) return;

    applySnapshot(nextState.data);
    editedCellsRef.current = { ...nextState.editedCells };
    setEditedCells(nextState.editedCells);
    setChangeSummary({});
    setHistoryIndex(targetIndex);
    historyIndexRef.current = targetIndex;
  }, [history, historyIndex, applySnapshot]);

  return {
    editedCells,
    validationErrors,
    historyIndex,
    historyLength: history.length,
    changeSummary,
    handleCellChange,
    handleUndo,
    handleRedo,
  };
}
